export const resolveMempoolBackendUrl = ({
  envUrl = process.env.REACT_APP_WS_BACKEND_URL,
  locationLike = typeof window !== 'undefined' ? window.location : null,
} = {}) => {
  if (envUrl && typeof envUrl === 'string' && envUrl.trim()) {
    return envUrl.trim();
  }

  if (locationLike?.protocol && locationLike?.host) {
    const wsProtocol = locationLike.protocol === 'https:' ? 'wss:' : 'ws:';
    const hostname = locationLike.hostname || locationLike.host.split(':')[0];
    const port = locationLike.port || locationLike.host.split(':')[1] || '';
    if (port && port !== '80' && port !== '443') {
      return `${wsProtocol}//${hostname}:8081/ws`;
    }
    return `${wsProtocol}//${locationLike.host}/ws`;
  }

  return 'ws://localhost:8081/ws';
};

export const appendSessionId = (url, sessionId) => {
  if (!sessionId) return url;

  const nextUrl = new URL(url);
  nextUrl.searchParams.set('session_id', sessionId);
  return nextUrl.toString();
};

export class MempoolWebSocketService {
  constructor() {
    this.ws = null;
    this.url = resolveMempoolBackendUrl();
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 10;
    this.reconnectDelay = 3000;
    this.reconnectTimer = null;
    this.listeners = new Map();
    this.isConnected = false;
    this.sessionId = null;
    this.lastError = '';
    this.lastHeartbeatAt = null;
    this.manualClose = false;
    this.nextRequestId = 1;
    this.pendingRequests = new Map();
    this.monitorConfig = null;
    this.monitorRequested = false;
    this.persistentTaskStatus = { tasks: [], session_id: '' };

    try {
      const saved = localStorage.getItem('mempool_session_id');
      if (saved) {
        this.sessionId = saved;
      }
    } catch (_) {}
  }

  connect() {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.emitState();
      return;
    }

    if (this.ws && this.ws.readyState === WebSocket.CONNECTING) {
      this.emitState();
      return;
    }

    this.manualClose = false;
    this.clearReconnectTimer();

    try {
      this.ws = new WebSocket(appendSessionId(this.url, this.sessionId));
      this.emitState({ state: 'connecting' });

      this.ws.onopen = () => {
        this.isConnected = true;
        this.reconnectAttempts = 0;
        this.lastError = '';
        this.lastHeartbeatAt = new Date().toISOString();
        this.emit('connected');
        this.emitState();
        this.send('task_list');
      };

      this.ws.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data);
          this.lastHeartbeatAt = new Date().toISOString();
          this.handleMessage(message);
          this.emitState();
        } catch (error) {
          this.lastError = '解析 WebSocket 訊息失敗';
          console.error('解析 WebSocket 訊息失敗:', error);
          this.emit('error', this.lastError);
          this.emitState();
        }
      };

      this.ws.onerror = () => {
        this.lastError = `無法連接到 mempool 後端 (${this.url})`;
        console.warn('Mempool WebSocket 錯誤:', this.lastError);
        this.emit('error', this.lastError);
        this.emitState();
      };

      this.ws.onclose = () => {
        this.isConnected = false;
        this.rejectPendingRequests('mempool backend connection closed');
        this.emit('disconnected');

        if (this.manualClose) {
          this.emitState();
          return;
        }

        this.scheduleReconnect();
      };
    } catch (error) {
      this.lastError = error?.message || String(error) || 'WebSocket 連線失敗';
      console.warn('Mempool WebSocket 連線失敗:', error);
      this.emit('error', this.lastError);
      this.scheduleReconnect();
    }
  }

  disconnect() {
    this.manualClose = true;
    this.clearReconnectTimer();
    this.rejectPendingRequests('mempool backend disconnected');

    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }

    this.isConnected = false;
    this.emitState({ state: 'disconnected' });
  }

  rejectPendingRequests(message = 'mempool backend connection closed') {
    for (const pending of this.pendingRequests.values()) {
      clearTimeout(pending.timeout);
      const error = new Error(message);
      error.code = 'WEBSOCKET_CLOSED';
      error.requestSent = pending.requestSent === true;
      pending.reject(error);
    }
    this.pendingRequests.clear();
  }

  waitForConnection(timeoutMs = 10000) {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      return Promise.resolve();
    }

    this.connect();

    return new Promise((resolve, reject) => {
      const cleanup = () => {
        clearTimeout(timeout);
        this.off('connected', handleConnected);
        this.off('error', handleError);
      };
      const handleConnected = () => {
        cleanup();
        resolve();
      };
      const handleError = (error) => {
        cleanup();
        reject(new Error(typeof error === 'string' ? error : (this.lastError || 'WebSocket connection failed')));
      };
      const timeout = setTimeout(() => {
        cleanup();
        reject(new Error(this.lastError || 'WebSocket connection timeout'));
      }, timeoutMs);

      this.on('connected', handleConnected);
      this.on('error', handleError);
    });
  }

  send(type, data = {}, requestId = '') {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      this.lastError = 'WebSocket 尚未連接，無法發送訊息';
      console.warn(this.lastError);
      this.emit('error', this.lastError);
      this.emitState();
      return false;
    }

    const message = {
      type,
      session_id: this.sessionId || undefined,
      request_id: requestId || undefined,
      data,
    };

    try {
      this.ws.send(JSON.stringify(message));
      return true;
    } catch (error) {
      this.lastError = error?.message || '發送 WebSocket 訊息失敗';
      console.error('發送 WebSocket 訊息失敗:', error);
      this.emit('error', this.lastError);
      this.emitState();
      return false;
    }
  }

  async request(type, data = {}, { timeoutMs = 30000 } = {}) {
    try {
      await this.waitForConnection(Math.min(timeoutMs, 10000));
    } catch (error) {
      const unsentError = error instanceof Error ? error : new Error(String(error || 'WebSocket connection failed'));
      unsentError.requestSent = false;
      throw unsentError;
    }
    const requestId = `${Date.now()}-${this.nextRequestId++}`;

    return new Promise((resolve, reject) => {
      const timeout = setTimeout(() => {
        this.pendingRequests.delete(requestId);
        const error = new Error(`${type} request timed out`);
        error.code = 'REQUEST_TIMEOUT';
        error.requestSent = true;
        reject(error);
      }, timeoutMs);

      const pending = { resolve, reject, timeout, requestSent: false };
      this.pendingRequests.set(requestId, pending);

      if (!this.send(type, data, requestId)) {
        clearTimeout(timeout);
        this.pendingRequests.delete(requestId);
        const error = new Error(this.lastError || `${type} request failed`);
        error.requestSent = false;
        reject(error);
      } else {
        pending.requestSent = true;
      }
    });
  }

  sendConfig(config) {
    const sent = this.send('config', config);
    if (sent) this.monitorConfig = { ...config };
    return sent;
  }

  start() {
    const sent = this.send('start');
    if (sent) this.monitorRequested = true;
    return sent;
  }

  stop() {
    this.monitorRequested = false;
    return this.send('stop');
  }

  replayMonitorState() {
    if (!this.monitorRequested || !this.monitorConfig) return false;
    if (!this.send('config', this.monitorConfig)) return false;
    return this.send('start');
  }

  handleMessage(message) {
    const {
      type,
      data,
      error,
      error_code: errorCode,
      error_definitive: errorDefinitive,
      request_id: requestId,
    } = message;

    if (requestId && this.pendingRequests.has(requestId)) {
      const pending = this.pendingRequests.get(requestId);
      clearTimeout(pending.timeout);
      this.pendingRequests.delete(requestId);
      if (error) {
        const backendError = new Error(typeof error === 'string' ? error : JSON.stringify(error));
        backendError.code = errorCode || error?.code;
        backendError.blockrazorDefinitive = errorDefinitive === true || error?.definitive === true;
        backendError.requestSent = true;
        pending.reject(backendError);
        return;
      } else {
        pending.resolve(data);
      }
    }

    if (error) {
      this.lastError = typeof error === 'string'
        ? error
        : (error?.message || JSON.stringify(error) || '後端回傳錯誤');
      console.error('後端錯誤:', this.lastError);
      this.emit('error', this.lastError);
      return;
    }

    const sessionId = message.session_id || data?.session_id;
    if (sessionId) {
      this.sessionId = String(sessionId);
      try {
        localStorage.setItem('mempool_session_id', this.sessionId);
      } catch (_) {}
    }

    switch (type) {
      case 'status':
        this.emit('status', data);
        break;
      case 'rpc_status':
        this.emit('rpc_status', data);
        break;
      case 'chain_read':
        this.emit('chain_read', data);
        break;
      case 'trade_execute':
        this.emit('trade_execute', data);
        break;
      case 'trade_status':
        this.emit('trade_status', data);
        break;
      case 'blockrazor_bundle':
        this.emit('blockrazor_bundle', data);
        break;
      case 'blockrazor_follow_bundle':
        this.emit('blockrazor_follow_bundle', data);
        break;
      case 'blockrazor_copy_subscribe':
        this.emit('blockrazor_copy_subscribe', data);
        break;
      case 'blockrazor_copy_unsubscribe':
        this.emit('blockrazor_copy_unsubscribe', data);
        break;
      case 'blockrazor_copy_match':
        this.emit('blockrazor_copy_match', data);
        break;
      case 'blockrazor_public_subscribe':
        this.emit('blockrazor_public_subscribe', data);
        break;
      case 'blockrazor_public_unsubscribe':
        this.emit('blockrazor_public_unsubscribe', data);
        break;
      case 'blockrazor_public_status':
        this.emit('blockrazor_public_status', data);
        break;
      case 'pending_tx':
        this.emit('pending_tx', data);
        break;
      case 'launchpad_activity':
        this.emit('launchpad_activity', data);
        break;
      case 'launchpad_copy_event':
        this.emit('launchpad_copy_event', data);
        this.emit('launchpad_task_event', data);
        break;
      case 'launchpad_task_event':
        this.emit('launchpad_task_event', data);
        this.emit('launchpad_copy_event', data);
        break;
      case 'matched_tx':
        this.emit('matched_tx', data);
        break;
      case 'rpc_speed_snapshot':
        this.emit('rpc_speed_snapshot', data);
        break;
      case 'task_status':
        this.persistentTaskStatus = data || { tasks: [], session_id: this.sessionId || '' };
        this.emit('task_status', this.persistentTaskStatus);
        break;
      case 'rpc_connect':
      case 'rpc_disconnect':
      case 'pending_subscribe':
      case 'pending_unsubscribe':
      case 'wallet_backup_upsert':
      case 'wallet_backup_status':
      case 'launchpad_copy_start':
      case 'launchpad_task_start':
      case 'config':
      case 'start':
      case 'stop':
        this.emit(type, data);
        break;
      case 'error':
        this.lastError = typeof data === 'string'
          ? data
          : (data?.message || JSON.stringify(data) || '後端回傳錯誤');
        this.emit('error', this.lastError);
        break;
      default:
        console.warn('未知 WebSocket 訊息類型:', type);
    }
  }

  scheduleReconnect() {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      this.lastError = '已達最大重連次數，請確認後端服務是否啟動';
      this.emit('error', this.lastError);
      this.emitState({ state: 'closed' });
      return;
    }

    this.reconnectAttempts += 1;
    const reconnectInfo = {
      attempt: this.reconnectAttempts,
      maxAttempts: this.maxReconnectAttempts,
      delayMs: this.reconnectDelay,
    };

    this.emit('reconnecting', reconnectInfo);
    this.emitState({
      state: 'reconnecting',
      nextReconnectInMs: this.reconnectDelay,
    });

    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      this.connect();
    }, this.reconnectDelay);
  }

  clearReconnectTimer() {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }

  on(event, callback) {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, []);
    }
    this.listeners.get(event).push(callback);
  }

  off(event, callback) {
    if (!this.listeners.has(event)) return;

    const callbacks = this.listeners.get(event);
    const index = callbacks.indexOf(callback);
    if (index > -1) {
      callbacks.splice(index, 1);
    }
  }

  emit(event, data) {
    if (!this.listeners.has(event)) return;

    this.listeners.get(event).forEach((callback) => {
      try {
        callback(data);
      } catch (error) {
        console.error('WebSocket listener failed:', error);
      }
    });
  }

  emitState(overrides = {}) {
    this.emit('state', this.getStatusSnapshot(overrides));
  }

  getStatusSnapshot(overrides = {}) {
    return {
      state: this.getConnectionState(),
      url: this.url,
      connected: this.isConnected,
      sessionId: this.sessionId,
      reconnectAttempts: this.reconnectAttempts,
      maxReconnectAttempts: this.maxReconnectAttempts,
      reconnectDelay: this.reconnectDelay,
      lastError: this.lastError,
      lastHeartbeatAt: this.lastHeartbeatAt,
      ...overrides,
    };
  }

  getPersistentTaskStatus() {
    return this.persistentTaskStatus;
  }

  async listPersistentTasks() {
    const status = await this.request('task_list', {}, { timeoutMs: 10000 });
    this.persistentTaskStatus = status || { tasks: [], session_id: this.sessionId || '' };
    return this.persistentTaskStatus;
  }

  async stopPersistentTask(taskId) {
    const status = await this.request('task_stop', { task_id: taskId }, { timeoutMs: 10000 });
    this.persistentTaskStatus = status || { tasks: [], session_id: this.sessionId || '' };
    return this.persistentTaskStatus;
  }

  getConnectionState() {
    if (!this.ws) return 'disconnected';
    switch (this.ws.readyState) {
      case WebSocket.CONNECTING:
        return 'connecting';
      case WebSocket.OPEN:
        return 'connected';
      case WebSocket.CLOSING:
        return 'closing';
      case WebSocket.CLOSED:
        return 'closed';
      default:
        return 'unknown';
    }
  }
}

export const mempoolWebSocketService = new MempoolWebSocketService();
